/**
 * @fileoverview UpgradeModal
 * @description Modal para mostrar opciones de upgrade de plan
 *              - Para mascotas: Standard y Premium
 *              - Para objetos: Solo Premium
 */

import React, { useState } from "react";
import { toast } from 'react-toastify';
import { getAuthHeaders, fetchWrapper } from "../services/api/utils/fetchWrapper";
import { API_URL } from "../services/api/config";

export default function UpgradeModal({ objeto, usuario, onClose }) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const esMascota = objeto?.tipo === 'mascota';

  // Planes disponibles
  const planesParaMascota = [
    {
      id: 'standard',
      nombre: 'STANDARD',
      precio: 250,
      precioRenovacion: 200,
      color: '#3B82F6',
      gradiente: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
      caracteristicas: [
        { texto: '5 recordatorios programados', icono: 'bi-bell-fill' },
        { texto: 'Asistente IA para cuidados', icono: 'bi-robot' },
        { texto: 'Directorio de proveedores', icono: 'bi-shop' }
      ]
    },
    {
      id: 'premium',
      nombre: 'PREMIUM',
      precio: 550,
      precioRenovacion: 350,
      color: '#8B5CF6',
      gradiente: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      destacado: true,
      caracteristicas: [
        { texto: 'Todo lo de Standard', icono: 'bi-check-circle-fill', esHeredado: true },
        { texto: '10 recordatorios programados', icono: 'bi-bell-fill' },
        { texto: 'Veterinarios y expediente', icono: 'bi-heart-pulse-fill' },
        { texto: 'Citas medicas', icono: 'bi-calendar-check-fill' },
        { texto: 'Recompensa por extravío', icono: 'bi-gift-fill' },
        { texto: 'Ubicación en tiempo real', icono: 'bi-geo-alt-fill' }
      ]
    }
  ];

  const planesParaObjeto = [
    {
      id: 'premium',
      nombre: 'PREMIUM',
      precio: 200,
      precioRenovacion: 200,
      color: '#8B5CF6',
      gradiente: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
      caracteristicas: [
        { texto: '5 recordatorios programados', icono: 'bi-bell-fill' },
        { texto: 'Asistente IA', icono: 'bi-robot' },
        { texto: 'Ofrecer recompensa', icono: 'bi-gift-fill' }
      ]
    }
  ];

  const planes = esMascota ? planesParaMascota : planesParaObjeto;

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    setEnviando(true);

    try {
      const headers = await getAuthHeaders();

      const response = await fetchWrapper(`${API_URL}/api/solicitudes-upgrade`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          objetoId: objeto.id,
          nombreObjeto: objeto.nombre || objeto.nombreMascota || 'Sin nombre',
          tipoObjeto: objeto.tipo,
          email: usuario?.correoPropietario || usuario?.email || '',
          telefono: usuario?.telefonoPropietario || usuario?.telefono || '',
          planActual: objeto.plan || 'light',
          planSolicitado: selectedPlan.id,
          precioUpgrade: selectedPlan.precio
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al enviar solicitud');
      }

      setEnviado(true);
      toast.success('Solicitud enviada correctamente');

    } catch (error) {
      console.error('[UpgradeModal] Error:', error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setEnviando(false);
    }
  };

  // Vista de confirmación exitosa
  if (enviado) {
    return (
      <div
        className="modal show"
        style={{
          display: "block",
          background: "rgba(0,0,0,0.6)",
          zIndex: 'var(--z-modal, 1050)'
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: '16px' }}>
            <div className="modal-body text-center p-5">
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px'
              }}>
                <i className="bi bi-check-lg" style={{ fontSize: '40px', color: 'white' }}></i>
              </div>

              <h4 style={{ fontWeight: '700', marginBottom: '12px' }}>
                ¡Gracias por tu interes!
              </h4>
              <p style={{ color: '#6B7280', marginBottom: '24px' }}>
                Te contactaremos en menos de 24 horas para darte todos los detalles del upgrade.
              </p>

              <button
                className="btn btn-primary"
                onClick={onClose}
                style={{
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '12px 32px',
                  fontWeight: '600'
                }}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista de confirmación de contacto
  if (showConfirmation) {
    return (
      <div
        className="modal show"
        style={{
          display: "block",
          background: "rgba(0,0,0,0.6)",
          zIndex: 'var(--z-modal, 1050)'
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content" style={{ borderRadius: '16px' }}>
            <div className="modal-header border-0 pb-0">
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowConfirmation(false)}
                disabled={enviando}
              ></button>
            </div>

            <div className="modal-body text-center px-4 pb-4">
              <div style={{
                width: '64px',
                height: '64px',
                background: '#F3F4F6',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <i className="bi bi-telephone-fill" style={{ fontSize: '28px', color: '#6B7280' }}></i>
              </div>

              <h5 style={{ fontWeight: '700', marginBottom: '16px' }}>
                Confirmar contacto
              </h5>
              <p style={{ color: '#6B7280', fontSize: '15px', marginBottom: '24px' }}>
                ¿Estas de acuerdo en que un asesor de LemmonTags te contacte por telefono para darte los detalles del upgrade?
              </p>

              <div className="d-flex gap-3 justify-content-center">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowConfirmation(false)}
                  disabled={enviando}
                  style={{
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontWeight: '500'
                  }}
                >
                  No
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirm}
                  disabled={enviando}
                  style={{
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 24px',
                    fontWeight: '600'
                  }}
                >
                  {enviando ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Enviando...
                    </>
                  ) : (
                    'Si, acepto'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Vista principal con planes
  return (
    <div
      className="modal show"
      style={{
        display: "block",
        background: "rgba(0,0,0,0.6)",
        zIndex: 'var(--z-modal, 1050)'
      }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)',
            padding: '24px',
            textAlign: 'center'
          }}>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
              style={{ position: 'absolute', right: '16px', top: '16px' }}
            ></button>

            <div style={{
              width: '56px',
              height: '56px',
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 12px'
            }}>
              <i className="bi bi-stars" style={{ fontSize: '28px', color: 'white' }}></i>
            </div>

            <h4 style={{ color: 'white', fontWeight: '700', marginBottom: '4px' }}>
              Mejora tu Plan
            </h4>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: 0 }}>
              {esMascota ? 'Desbloquea mas beneficios para tu mascota' : 'Obtén mas funciones para tu objeto'}
            </p>
          </div>

          {/* Body con planes */}
          <div className="modal-body p-4">
            <div className={`row g-3 ${planes.length === 1 ? 'justify-content-center' : ''}`}>
              {planes.map((plan) => (
                <div key={plan.id} className={planes.length === 1 ? 'col-md-8' : 'col-md-6'}>
                  <div
                    style={{
                      border: plan.destacado ? `2px solid ${plan.color}` : '1px solid #E5E7EB',
                      borderRadius: '16px',
                      padding: '20px',
                      height: '100%',
                      position: 'relative',
                      background: plan.destacado ? 'linear-gradient(180deg, #F5F3FF 0%, #FFFFFF 100%)' : 'white'
                    }}
                  >
                    {/* Badge destacado */}
                    {plan.destacado && (
                      <div style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: plan.gradiente,
                        color: 'white',
                        padding: '4px 16px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Recomendado
                      </div>
                    )}

                    {/* Nombre del plan */}
                    <h5 style={{
                      color: plan.color,
                      fontWeight: '800',
                      fontSize: '18px',
                      marginBottom: '8px',
                      marginTop: plan.destacado ? '8px' : 0
                    }}>
                      {plan.nombre}
                    </h5>

                    {/* Precio */}
                    <div style={{ marginBottom: '16px' }}>
                      <span style={{ fontSize: '32px', fontWeight: '800', color: '#1F2937' }}>
                        ${plan.precio}
                      </span>
                      <span style={{ color: '#6B7280', fontSize: '14px' }}> MXN</span>
                      <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                        Despues ${plan.precioRenovacion}/año
                      </div>
                    </div>

                    {/* Características */}
                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0' }}>
                      {plan.caracteristicas.map((car, idx) => (
                        <li
                          key={idx}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '6px 0',
                            fontSize: '13px',
                            color: car.esHeredado ? '#9CA3AF' : '#374151'
                          }}
                        >
                          <i
                            className={`bi ${car.icono}`}
                            style={{
                              color: car.esHeredado ? '#9CA3AF' : plan.color,
                              fontSize: '14px'
                            }}
                          ></i>
                          {car.texto}
                        </li>
                      ))}
                    </ul>

                    {/* Botón */}
                    <button
                      className="btn w-100"
                      onClick={() => handleSelectPlan(plan)}
                      style={{
                        background: plan.destacado ? plan.gradiente : 'transparent',
                        color: plan.destacado ? 'white' : plan.color,
                        border: plan.destacado ? 'none' : `2px solid ${plan.color}`,
                        borderRadius: '12px',
                        padding: '12px',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                    >
                      Me interesa
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Nota al pie */}
            <p style={{
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: '12px',
              marginTop: '20px',
              marginBottom: 0
            }}>
              <i className="bi bi-shield-check me-1"></i>
              Pago seguro • Cancela cuando quieras
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
